import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "@/lib/api";
import { MapPin, Buildings, Calendar, ImageSquare } from "@phosphor-icons/react";

/**
 * PublicPhotos — no-auth landing page for a shared photo bundle.
 *
 * Landlords/tenants receive a link like `/public/photos/<token>` from
 * office. They see all Before/After photos for a WO, with captions and
 * timestamps, in a print-friendly gallery. No login. The share can be
 * revoked from Field Radar admin at any time.
 *
 * Photo URLs from the API are relative (`/api/files/...`) so we glue
 * on `REACT_APP_BACKEND_URL` (imported as API) here — otherwise the
 * <img> tags would 404 when served from a preview domain.
 */
function absUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  // API already ends with `/api`; server returns `/api/files/...`
  const base = API.replace(/\/api$/, "");
  return `${base}${u}`;
}

export default function PublicPhotos() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [expandBefore, setExpandBefore] = useState(true);
  const [expandAfter, setExpandAfter] = useState(true);

  useEffect(() => {
    fetch(`${API}/public/photos/${token}`)
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${r.status}`);
        }
        return r.json();
      })
      .then(setData)
      .catch((e) => setErr(e.message));
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#0F1F3A] text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="font-display font-black text-3xl mb-3">Link not available</h1>
          <p className="text-white/70">{err}</p>
          <Link to="/" className="mt-6 inline-block text-[#7FE8ED] hover:underline text-sm font-black uppercase tracking-widest">Back to Field Radar</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500 font-black uppercase tracking-widest text-sm">Loading…</div>
      </div>
    );
  }

  const before = data.photos.filter(p => p.kind === "before");
  const after  = data.photos.filter(p => p.kind === "after");
  const other  = data.photos.filter(p => p.kind !== "before" && p.kind !== "after");
  const total  = data.photos.length;
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return ""; }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-photos-page">
      <header className="bg-[#0F1F3A] text-white">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#7FE8ED]">
            {data.company} · Photo Report
          </div>
          <h1 className="mt-2 font-display font-black text-3xl md:text-4xl leading-tight">
            {data.work_order_number ? `WO #${data.work_order_number} · ` : ""}{data.title}
          </h1>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/80">
            {data.address && (
              <div className="inline-flex items-center gap-1.5">
                <MapPin weight="fill" size={14} className="text-[#7FE8ED]"/>
                {data.address}{data.unit ? ` · Apt ${data.unit}` : ""}
              </div>
            )}
            <div className="inline-flex items-center gap-1.5">
              <ImageSquare weight="fill" size={14} className="text-[#7FE8ED]"/>
              {total} photo{total === 1 ? "" : "s"}
            </div>
            {data.generated_by && (
              <div className="inline-flex items-center gap-1.5">
                <Buildings weight="fill" size={14} className="text-[#7FE8ED]"/>
                Shared by {data.generated_by}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {total === 0 && (
          <div className="text-center py-20">
            <div className="text-5xl mb-3">📷</div>
            <div className="font-display font-black text-xl">No photos on this work order yet</div>
            <div className="text-slate-500 text-sm mt-1">Check back later — the crew will be uploading soon.</div>
          </div>
        )}

        {before.length > 0 && <Section title="Before" count={before.length} kind="before" photos={before} onOpen={setLightbox} fmt={fmt} expanded={expandBefore} setExpanded={setExpandBefore}/>}
        {after.length > 0  && <Section title="After Repair" count={after.length} kind="after" photos={after} onOpen={setLightbox} fmt={fmt} expanded={expandAfter} setExpanded={setExpandAfter}/>}
        {other.length > 0  && <Section title="Other" count={other.length} kind="other" photos={other} onOpen={setLightbox} fmt={fmt} expanded={true} setExpanded={()=>{}}/>}

        <footer className="mt-16 pt-8 border-t border-slate-200 text-center text-xs text-slate-400">
          <div>Generated by Field Radar · {data.company}</div>
          {data.expires_at && (
            <div className="mt-1">This link expires {new Date(data.expires_at).toLocaleDateString()}.</div>
          )}
        </footer>
      </main>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="public-photos-lightbox">
          <div onClick={(e) => e.stopPropagation()} className="max-w-4xl w-full">
            <img src={absUrl(lightbox.url)} alt="" className="max-h-[80vh] mx-auto rounded-2xl"/>
            <div className="mt-4 text-center text-white/90">
              {lightbox.caption && <div className="font-black text-lg">{lightbox.caption}</div>}
              <div className="text-sm text-white/60 capitalize mt-1">{lightbox.kind} · {fmt(lightbox.at)}</div>
            </div>
          </div>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl">×</button>
        </div>
      )}
    </div>
  );
}

function Section({ title, count, kind, photos, onOpen, fmt, expanded, setExpanded }) {
  return (
    <section className="mb-10" data-testid={`public-photos-section-${kind}`}>
      <button onClick={() => setExpanded(v => !v)} className="w-full flex items-center gap-3 mb-4">
        <h2 className="font-display font-black text-2xl">{title}</h2>
        <span className={`inline-flex items-center h-7 px-3 rounded-full text-[10px] font-black uppercase tracking-widest ${
          kind === "before" ? "bg-amber-100 text-amber-800" : kind === "after" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"
        }`}>
          {count} photo{count === 1 ? "" : "s"}
        </span>
        <span className="ml-auto text-slate-400 text-xl">{expanded ? "−" : "+"}</span>
      </button>
      {expanded && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {photos.map((p, i) => (
            <button key={i} onClick={() => onOpen(p)}
              data-testid={`public-photos-tile-${kind}-${i}`}
              className="group relative aspect-square rounded-xl overflow-hidden bg-slate-100 hover:ring-2 hover:ring-[#00C4CC] transition">
              <img src={absUrl(p.url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
              {p.annotated && (
                <span className="absolute top-2 left-2 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#00C4CC] text-[#0F1F3A]">
                  ✏️ Marked
                </span>
              )}
              {p.caption && (
                <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-gradient-to-t from-[#0F1F3A]/95 to-transparent text-[11px] font-black text-white line-clamp-2 leading-tight">
                  {p.caption}
                </div>
              )}
              {p.at && !p.caption && (
                <div className="absolute bottom-1.5 right-1.5 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-black/60 text-white inline-flex items-center gap-1">
                  <Calendar size={9} weight="fill"/> {fmt(p.at)}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
